/**
 * Custom hook for fetching currencies
 * Extracted from Activity.tsx to improve code organization
 */

import { useEffect, useState, useRef } from "react";
import { auth } from "../../../firebase";
import {
  collection,
  query,
  doc,
  onSnapshot,
  getDocs,
  getDoc,
  QuerySnapshot,
  Unsubscribe,
  FirestoreError,
  DocumentSnapshot,
  Timestamp,
} from "firebase/firestore";
import { WORKSPACE_SETTINGS_DOC_ID } from "../../../utils/firestorePaths";
import { db } from "../../../firebase";
import { Currency, WorkspaceSettings } from "../../../types";
import { isDemoMode } from "../../../utils/demoMode";
import { isBypassAuthEnabled } from "../../../utils/bypassAuth";
import { sortCurrenciesByOrder } from "../../../utils/currencyOrdering";

export interface UseCurrenciesResult {
  currencies: Currency[];
  defaultCurrency: string;
  currenciesLoaded: boolean;
}

export interface UseCurrenciesParams {
  workspaceId: string;
}

// Cache for currencies per workspace - persists across component unmounts
const currenciesCache = new Map<string, {
  currencies: Currency[];
  defaultCurrency: string;
  currencyOrder: string[];
  timestamp: number;
}>();

// Cache expiration time: 5 minutes
const CACHE_EXPIRY_MS = 5 * 60 * 1000;

/**
 * Hook for fetching currencies and default currency in real-time
 * Uses caching to avoid reloading data when navigating back to Activity
 */
export const useCurrencies = ({ workspaceId }: UseCurrenciesParams): UseCurrenciesResult => {
  // Check cache first
  const cachedData = currenciesCache.get(workspaceId);
  const hasValidCache = !!(cachedData && (Date.now() - cachedData.timestamp) < CACHE_EXPIRY_MS);
  
  const [currencies, setCurrencies] = useState<Currency[]>(
    hasValidCache && cachedData ? cachedData.currencies : []
  );
  const [defaultCurrency, setDefaultCurrency] = useState<string>(
    hasValidCache && cachedData ? cachedData.defaultCurrency : "USD"
  );
  const [currenciesLoaded, setCurrenciesLoaded] = useState<boolean>(hasValidCache);
  const [currencyOrder, setCurrencyOrder] = useState<string[]>(
    hasValidCache && cachedData ? cachedData.currencyOrder : []
  );
  const defaultCurrencyRef = useRef<string>(
    hasValidCache && cachedData ? cachedData.defaultCurrency : "USD"
  );

  useEffect(() => {
    // Allow loading in demo mode, bypass auth mode, or when authenticated
    if (!workspaceId || (!auth.currentUser && !isDemoMode() && !isBypassAuthEnabled())) return;

    // Check cache again inside effect
    const cachedData = currenciesCache.get(workspaceId);
    const hasValidCache = !!(cachedData && (Date.now() - cachedData.timestamp) < CACHE_EXPIRY_MS);

    // Only set loading if we don't have cached data
    if (!hasValidCache) {
      setCurrenciesLoaded(false);
    }

    // Fetch default currency and currency order
    // IMPORTANT: Must use WORKSPACE_SETTINGS_DOC_ID constant to match backend
    const settingsRef = doc(db, "workspaces", workspaceId, "settings", WORKSPACE_SETTINGS_DOC_ID);
    const unsubscribeSettings = onSnapshot(settingsRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as WorkspaceSettings;
        const newDefaultCurrency = data.defaultCurrency || "USD";
        const newCurrencyOrder = data.currencyOrder || [];
        defaultCurrencyRef.current = newDefaultCurrency;
        setDefaultCurrency(newDefaultCurrency);
        setCurrencyOrder(newCurrencyOrder);
        
        // Update cache when settings change
        const currentCache = currenciesCache.get(workspaceId);
        if (currentCache) {
          currenciesCache.set(workspaceId, {
            ...currentCache,
            defaultCurrency: newDefaultCurrency,
            currencyOrder: newCurrencyOrder,
          });
        }
      } else {
        setCurrencyOrder([]);
      }
    });

    // Fetch currencies
    const currenciesRef = collection(db, "workspaces", workspaceId, "currencies");
    const basicQuery = query(currenciesRef);
    
    const unsubscribeCurrencies = onSnapshot(
      basicQuery,
      async (snapshot) => {
        const currentDefault = defaultCurrencyRef.current;
        const currenciesList: Currency[] = snapshot.docs
          .map((docSnap: DocumentSnapshot) => {
            const data = docSnap.data();
            if (!data) return null;
            
            const rates = (data.rates as Record<string, number | null>) || {};
            let currentRate: number | null;
            if (rates[currentDefault] !== undefined) {
              currentRate = rates[currentDefault];
            } else if (typeof data.rate === "number") {
              currentRate = data.rate;
            } else if (data.rate === null || data.rate === undefined) {
              currentRate = null;
            } else {
              const parsed = parseFloat(String(data.rate));
              currentRate = isNaN(parsed) ? null : parsed;
            }
            
            return {
              code: docSnap.id,
              name: (data.name as string) || "",
              symbol: (data.symbol as string) || "",
              rate: currentRate,
              rates: rates,
              decimalPlaces: data.decimalPlaces !== undefined ? (data.decimalPlaces as number) : undefined,
              updatedAt: data.updatedAt || data.createdAt || Timestamp.now(),
            } as Currency;
          })
          .filter((c): c is Currency => c !== null);
        
        // Check if default currency is in the list, if not, fetch it separately
        const defaultCurrencyInList = currenciesList.find(c => c.code === currentDefault);
        if (!defaultCurrencyInList) {
          try {
            const defaultCurrencyDocRef = doc(db, "workspaces", workspaceId, "currencies", currentDefault);
            const defaultCurrencyDoc = await getDoc(defaultCurrencyDocRef);
            if (defaultCurrencyDoc.exists()) {
              const data = defaultCurrencyDoc.data();
              const rates = (data.rates as Record<string, number | null>) || {};
              const currentRate = rates[currentDefault] ?? (typeof data.rate === "number" ? data.rate : 1.0);
              currenciesList.push({
                code: currentDefault,
                name: (data.name as string) || "",
                symbol: (data.symbol as string) || "",
                rate: currentRate,
                rates: rates,
                decimalPlaces: data.decimalPlaces !== undefined ? (data.decimalPlaces as number) : undefined,
                updatedAt: data.updatedAt || data.createdAt || Timestamp.now(),
              } as Currency);
            }
          } catch (error) {
            console.error("Error fetching default currency:", error);
          }
        }
        
        // Sort according to workspace currency order
        const sortedCurrencies = sortCurrenciesByOrder(currenciesList, currencyOrder, currentDefault);
        
        // Update cache
        currenciesCache.set(workspaceId, {
          currencies: sortedCurrencies,
          defaultCurrency: currentDefault,
          currencyOrder: currencyOrder,
          timestamp: Date.now(),
        });
        
        setCurrencies(sortedCurrencies);
        setCurrenciesLoaded(true);
      },
      (error: FirestoreError) => {
        console.error("Error fetching currencies:", error);
        getDocs(currenciesRef)
          .then(async (snapshot) => {
            const currentDefault = defaultCurrencyRef.current;
            const currenciesList: Currency[] = snapshot.docs
              .map((docSnap: DocumentSnapshot) => {
                const data = docSnap.data();
                if (!data) return null;
                const rates = (data.rates as Record<string, number | null>) || {};
                const currentRate = rates[currentDefault] ?? (typeof data.rate === "number" ? data.rate : null);
                return {
                  code: docSnap.id,
                  name: (data.name as string) || "",
                  symbol: (data.symbol as string) || "",
                  rate: currentRate,
                  rates: rates,
                  decimalPlaces: data.decimalPlaces !== undefined ? (data.decimalPlaces as number) : undefined,
                  updatedAt: data.updatedAt || data.createdAt || Timestamp.now(),
                } as Currency;
              })
              .filter((c): c is Currency => c !== null);
            
            // Check if default currency is in the list, if not, fetch it separately
            const defaultCurrencyInList = currenciesList.find(c => c.code === currentDefault);
            if (!defaultCurrencyInList) {
              try {
                const defaultCurrencyDocRef = doc(db, "workspaces", workspaceId, "currencies", currentDefault);
                const defaultCurrencyDoc = await getDoc(defaultCurrencyDocRef);
                if (defaultCurrencyDoc.exists()) {
                  const data = defaultCurrencyDoc.data();
                  const rates = (data.rates as Record<string, number | null>) || {};
                  const currentRate = rates[currentDefault] ?? (typeof data.rate === "number" ? data.rate : 1.0);
                  currenciesList.push({
                    code: currentDefault,
                    name: (data.name as string) || "",
                    symbol: (data.symbol as string) || "",
                    rate: currentRate,
                    rates: rates,
                    decimalPlaces: data.decimalPlaces !== undefined ? (data.decimalPlaces as number) : undefined,
                    updatedAt: data.updatedAt || data.createdAt || Timestamp.now(),
                  } as Currency);
                }
              } catch (error) {
                console.error("Error fetching default currency:", error);
              }
            }
            
            // Sort according to workspace currency order
            const sortedCurrencies = sortCurrenciesByOrder(currenciesList, currencyOrder, currentDefault);
            
            // Update cache
            currenciesCache.set(workspaceId, {
              currencies: sortedCurrencies,
              defaultCurrency: currentDefault,
              currencyOrder: currencyOrder,
              timestamp: Date.now(),
            });
            
            setCurrencies(sortedCurrencies);
            setCurrenciesLoaded(true);
          })
          .catch(() => {
            setCurrencies([]);
            setCurrenciesLoaded(true);
          });
      }
    );

    return () => {
      unsubscribeSettings();
      unsubscribeCurrencies();
    };
  }, [workspaceId, currencyOrder]); // Remove defaultCurrency from deps since we use ref

  return {
    currencies,
    defaultCurrency,
    currenciesLoaded,
  };
};
